import {
  useCanAccess,
  useGetIdentity,
  useLocaleState,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  WithRecord,
} from "ra-core";
import { useEffect, useState } from "react";
import { TextField } from "@/components/admin/text-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatLocalizedDate } from "../misc/RelativeDate";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { Contact, Sale } from "../types";
import { getLeadSlaLevel } from "./contactModel";
import { getSupabaseClient } from "../providers/supabase/supabase";

export const ContactBackgroundInfo = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [locale = "en"] = useLocaleState();
  const { identity } = useGetIdentity();
  const [reassigning, setReassigning] = useState(false);
  const [agents, setAgents] = useState<Sale[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const { canAccess: canReassign } = useCanAccess({
    resource: "sales",
    action: "edit",
  });
  const isCurrentUser = record?.sales_id === identity?.id;
  const salesName = useGetSalesName(record?.sales_id, {
    enabled: !isCurrentUser,
  });

  useEffect(() => {
    if (!canReassign) return;
    let cancelled = false;
    void (async () => {
      const { data } = await getSupabaseClient()
        .from("sales")
        .select("id, first_name, last_name")
        .eq("disabled", false)
        .order("first_name");
      if (cancelled) return;
      const rows = (data ?? []) as Sale[];
      setAgents(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [canReassign]);

  useEffect(() => {
    if (!record) return;
    // Prefer a different agent than current owner when assigning.
    const other = agents.find((a) => String(a.id) !== String(record.sales_id));
    setSelectedAgentId(
      other
        ? String(other.id)
        : record.sales_id != null
          ? String(record.sales_id)
          : "",
    );
  }, [agents, record?.id, record?.sales_id]);

  if (!record) return null;

  const formattedLastSeen = record.last_seen
    ? formatLocalizedDate(record.last_seen, locale)
    : "";
  const formattedFirstSeen = formatLocalizedDate(record.first_seen, locale);
  const sla = getLeadSlaLevel(record);

  const handleAssign = async () => {
    if (!canReassign) {
      notify("resources.contacts.sla.agents_only", { type: "warning" });
      return;
    }
    if (!selectedAgentId) {
      notify("resources.contacts.sla.reassign_error", { type: "warning" });
      return;
    }
    if (String(selectedAgentId) === String(record.sales_id)) {
      notify("resources.contacts.sla.reassign_ok", { type: "info" });
      return;
    }
    setReassigning(true);
    try {
      const { data, error } = await getSupabaseClient().rpc(
        "transfer_contact",
        {
          p_contact_id: Number(record.id),
          p_new_sales_id: Number(selectedAgentId),
        },
      );
      if (error) throw error;
      if (
        data &&
        typeof data === "object" &&
        (data as { ok?: boolean }).ok === false
      ) {
        throw new Error(
          (data as { error?: string }).error || "transfer failed",
        );
      }
      notify("resources.contacts.sla.reassign_ok", { type: "info" });
      refresh();
    } catch {
      notify("resources.contacts.sla.reassign_error", { type: "warning" });
    } finally {
      setReassigning(false);
    }
  };

  return (
    <div>
      <WithRecord<Contact>
        render={(rec) =>
          rec?.background ? (
            <div className="pb-2 text-sm">
              <TextField source="background" record={rec} />
            </div>
          ) : null
        }
      />
      {sla && (
        <div className="pb-2 flex flex-wrap items-center gap-2">
          <Badge
            variant="secondary"
            className={
              sla === "overdue"
                ? "bg-red-100 text-red-800"
                : sla === "warn"
                  ? "bg-amber-100 text-amber-900"
                  : "bg-emerald-100 text-emerald-800"
            }
          >
            {translate(`resources.contacts.sla.${sla}`)}
          </Badge>
        </div>
      )}
      <div className="text-muted-foreground md:py-0.5">
        <span className="text-sm">
          {translate("resources.contacts.background.added_on", {
            date: formattedFirstSeen,
          })}
        </span>{" "}
      </div>

      <div className="text-muted-foreground md:py-0.5">
        <span className="text-sm">
          {translate("resources.contacts.background.last_activity_on", {
            date: formattedLastSeen,
          })}
        </span>
      </div>

      <div className="inline-flex text-muted-foreground text-sm md:py-0.5">
        {translate(
          isCurrentUser
            ? "resources.contacts.background.followed_by_you"
            : "resources.contacts.background.followed_by",
          { name: salesName },
        )}
      </div>

      {canReassign && (
        <div className="pt-3 space-y-2">
          <label className="text-sm font-medium text-foreground block">
            {translate("resources.contacts.sla.assign_to", {
              _: "Assign to agent",
            })}
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select
              value={selectedAgentId}
              onValueChange={setSelectedAgentId}
              disabled={reassigning || agents.length === 0}
            >
              <SelectTrigger className="h-8 w-full sm:max-w-xs">
                <SelectValue
                  placeholder={translate(
                    "resources.contacts.sla.assign_placeholder",
                    { _: "Select an agent…" },
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={String(agent.id)} value={String(agent.id)}>
                    {`${agent.first_name} ${agent.last_name}`.trim()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={
                reassigning ||
                !selectedAgentId ||
                String(selectedAgentId) === String(record.sales_id)
              }
              onClick={handleAssign}
            >
              {translate("resources.contacts.sla.assign_confirm", {
                _: "Assign",
              })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
